import React, { useState } from 'react';
import styles from './styles.module.scss';
import CustomInput from '../../../../../components/CustomInput';
import Button from '../../../../../components/Button';
import { commonImages, operatorImages } from '../../../../../assets/images';
import DateRangePicker from 'react-daterange-picker';
import 'react-daterange-picker/dist/css/react-calendar.css';
import moment from 'moment';
import { TRANSACTION_REPORT_TABS } from '../../constants';

const TransactionReportForm = ({ email, emailError, dates, onChange, onClick }) => {
    const [showDate, setShowDate] = useState(false);

    const onSelect = selectedDates => {
        onChange('date', {
            startDate: moment(selectedDates.start).format('YYYY-MM-DD'),
            endDate: moment(selectedDates.end).format('YYYY-MM-DD'),
            dates: selectedDates,
        });
        setShowDate(false);
    };

    const toggleDate = () => {
        setShowDate(!showDate);
    };

    const formatDate = date => moment(date).format('MMM Do YY');

    const minimumDate = moment().subtract(6, 'months').toDate();
    const maximumDate = new Date();

    return (
        <div className={styles.container}>
            <div className={styles.content}>
                <div className={styles.header}>
                    <p className={styles.subtitle}>
                        Enter email and date range to generate report (max 6 months old)
                    </p>
                </div>

                <div className={styles.formCard}>
                    <div className={styles.inputContainer}>
                        <CustomInput
                            inputLable="Email Address *"
                            placeholder="Enter email address"
                            value={email}
                            onTextChange={value => onChange('email', value)}
                            error={emailError}
                            required={true}
                        />
                    </div>

                    <div className={styles.inputContainer}>
                        <label className={styles.inputLabel}>Date Range *</label>
                        <div className={styles.dateWrapper}>
                            <Button
                                onClick={toggleDate}
                                buttonStyle="secondary"
                                label={
                                    dates
                                        ? `${formatDate(dates.start)} - ${formatDate(dates.end)}`
                                        : 'Select Date Range'
                                }
                                className={styles.dateBtn}
                            />
                            {showDate ? (
                                <div className={styles.datePicker}>
                                    <DateRangePicker
                                        numberOfCalendars={1}
                                        selectionType="range"
                                        onSelect={onSelect}
                                        className={styles.dateRange}
                                        value={dates}
                                        singleDateRange={true}
                                        minimumDate={minimumDate}
                                        maximumDate={maximumDate}
                                    />
                                </div>
                            ) : null}
                        </div>
                    </div>

                    <div className={styles.buttonContainer}>
                        <Button
                            label="Send Report"
                            buttonStyle="primary"
                            onClick={() => onClick('sendReport')}
                            className={styles.submitButton}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

const GeneratedReportsTab = ({ onClick }) => {
    return (
        <div className={styles.container}>
            <div className={styles.content}>
                <div className={styles.formCard}>
                    <p className={styles.subtitle}>
                        View and download generated transaction reports on desktop for the
                        full list. You can refresh from the Generated Reports tab there.
                    </p>
                    <div className={styles.buttonContainer}>
                        <Button
                            label="Refresh"
                            buttonStyle="primary"
                            onClick={() => onClick('refreshReports')}
                            className={styles.submitButton}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

const SuccessPage = ({ onClick }) => {
    return (
        <div className={styles.container}>
            <div className={styles.content}>
                <div className={styles.statusCard}>
                    <div className={styles.statusIcon}>
                        <img
                            src={operatorImages.successIcon}
                            alt="Success"
                            className={styles.iconImage}
                        />
                    </div>
                    <h2 className={styles.statusTitle}>Report generation started!</h2>
                    <p className={styles.statusMessage}>
                        The transaction report is being prepared. You will receive it by
                        email when it is ready. You can also download it later from the
                        Generated Reports section.
                    </p>
                    <div className={styles.statusActions}>
                        <Button
                            label="View Generated Reports"
                            buttonStyle="primary"
                            onClick={() => onClick('viewGeneratedReports')}
                            className={styles.actionButton}
                        />
                        <Button
                            label="Okay"
                            buttonStyle="outline"
                            onClick={() => onClick('backToForm')}
                            className={styles.actionButton}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

const FailurePage = ({ onClick }) => {
    return (
        <div className={styles.container}>
            <div className={styles.content}>
                <div className={styles.statusCard}>
                    <div className={styles.statusIcon}>
                        <img
                            src={commonImages.errorIcon}
                            alt="Error"
                            className={styles.iconImage}
                        />
                    </div>
                    <h2 className={styles.statusTitle}>Failed to Send Report</h2>
                    <p className={styles.statusMessage}>
                        There was an error sending the report. Please try again.
                    </p>
                    <div className={styles.statusActions}>
                        <Button
                            label="Try Again"
                            buttonStyle="primary"
                            onClick={() => onClick('retry')}
                            className={styles.actionButton}
                        />
                        <Button
                            label="Okay"
                            buttonStyle="outline"
                            onClick={() => onClick('backToForm')}
                            className={styles.actionButton}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

const PageTabs = ({ transactionReportTabs, activeTab, onClick }) => (
    <div className={styles.tabsRow}>
        {transactionReportTabs.map(tab => (
            <button
                key={tab.id}
                type="button"
                className={[
                    styles.tabButton,
                    activeTab === tab.id ? styles.tabButtonActive : '',
                ].join(' ')}
                onClick={() => onClick('switchTab', tab.id)}>
                {tab.label}
            </button>
        ))}
    </div>
);

export default function Mobile(props) {
    const { showSuccess, showFailure, activeTab, transactionReportTabs, onClick } =
        props;

    if (showSuccess) {
        return <SuccessPage {...props} />;
    }

    if (showFailure) {
        return <FailurePage {...props} />;
    }

    return (
        <div>
            <div className={styles.mobileHeader}>
                <h1 className={styles.title}>Transaction Report</h1>
            </div>
            <PageTabs
                transactionReportTabs={transactionReportTabs}
                activeTab={activeTab}
                onClick={onClick}
            />
            {activeTab === TRANSACTION_REPORT_TABS.GENERATED ? (
                <GeneratedReportsTab {...props} />
            ) : (
                <TransactionReportForm {...props} />
            )}
        </div>
    );
}
